import React, { useState } from 'react';
import ResultSummary from './ResultSummary';
import AmortizationTable from './AmortizationTable';
import ExportOptions from './ExportOptions';
import { formatCurrency } from '../../utils/calculations';
import { exportData } from '../../utils/exportUtils';

const ResultsPanel = ({ results }) => {
  const [showAmortizationTable, setShowAmortizationTable] = useState(false);
  
  const handleExport = (format) => {
    exportData(results, format, results.calculatorType);
  };
  
  if (!results) return null;

  // Determine which schedule to show for early repayment
  const getScheduleForDisplay = () => {
    if (results.calculatorType === 'early') {
      // For early repayment, show the reduce payment scenario schedule if available
      if (results.reducePaymentScenario && results.reducePaymentScenario.schedule) {
        return results.reducePaymentScenario.schedule;
      }
      // Otherwise show reduce term scenario schedule if available
      if (results.reduceTermScenario && results.reduceTermScenario.schedule) {
        return results.reduceTermScenario.schedule;
      }
      // If no scenarios available, return empty array
      return [];
    }
    // For other calculator types, use the main schedule
    return results.schedule || [];
  };

  const scheduleToShow = getScheduleForDisplay();
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden">
      <div className="bg-blue-600 text-white px-6 py-4">
        <h2 className="text-xl font-bold">Calculation Results</h2>
        <p className="text-blue-100 mt-1">
          {results.calculatorType === 'standard' && 'Standard Repayment Schedule'}
          {results.calculatorType === 'missed' && 'Missed Payment Simulation'}
          {results.calculatorType === 'interest' && 'Interest Accrual Analysis'}
          {results.calculatorType === 'restructure' && 'Repayment Restructuring Plan'}
          {results.calculatorType === 'early' && 'Early Repayment Analysis'}
        </p>
      </div>
      
      <div className="p-6">
        <ResultSummary results={results} />
        
        <div className="mt-8 flex flex-wrap justify-between items-center">
          {scheduleToShow.length > 0 && (
            <button
              onClick={() => setShowAmortizationTable(!showAmortizationTable)}
              className="btn btn-secondary mb-4 md:mb-0 cursor-pointer"
            >
              {showAmortizationTable ? 'Hide' : 'Show'} 
              {results.calculatorType === 'early' ? ' Example Schedule' : ' Amortization Schedule'}
            </button>
          )}
          
          <ExportOptions onExport={handleExport} />
        </div>
        
        {showAmortizationTable && scheduleToShow.length > 0 && (
          <div className="mt-6">
            {results.calculatorType === 'early' && (
              <p className="text-sm text-gray-600 mb-3">
                {results.reducePaymentScenario && results.reducePaymentScenario.schedule ? 
                  'Showing schedule for reduced payment scenario' : 
                  'Showing schedule for reduced term scenario'}
              </p>
            )}
            <AmortizationTable 
              schedule={scheduleToShow} 
              calculatorType={results.calculatorType}
              dateInfo={results.dateInfo}
            />
          </div>
        )}
        
        {/* Regulatory Warnings and Information */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Important Information</h3>
          
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <p className="text-sm text-gray-700 mb-2">
              This calculator is provided for informational purposes only and does not constitute financial advice.
            </p>
            
            {/* Enhanced APR Explanation - not shown for early repayment */}
            {results.calculatorType !== 'early' && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-blue-600 mb-3">Understanding APR vs. Annual Interest Rate</h4>
                
                <p className="text-sm text-gray-700 mb-3">
                  The Annual Percentage Rate (APR) represents the true yearly cost of borrowing, while the nominal annual interest rate is the stated rate. 
                  Even when no additional fees are charged, the APR is typically higher than the stated annual rate due to the timing of loan repayments.
                </p>

                <h5 className="font-medium text-gray-800 mb-2">Why APR is Higher Even Without Fees:</h5>

                <div className="space-y-2 mb-3">
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <div>
                      <span className="font-medium text-gray-800">Payment Timing:</span> 
                      <span className="text-gray-700"> With monthly payments, you're paying money back earlier than you would with a single annual payment, so you don't get to keep the full loan amount for the entire year.</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <div>
                      <span className="font-medium text-gray-800">Effective Cost:</span> 
                      <span className="text-gray-700"> The APR calculates what single annual interest rate would cost the same as making monthly payments throughout the year.</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <div>
                      <span className="font-medium text-gray-800">Time Value Impact:</span> 
                      <span className="text-gray-700"> Because you're returning money to the lender monthly rather than keeping it for the full term, the effective annual cost is higher than the nominal rate suggests.</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-700 mb-3">
                  <span className="font-medium text-gray-800">Concrete Example:</span> A loan with a 5% annual rate paid monthly will have a higher APR because you're paying back principal throughout the year rather than benefiting from having the full amount for 12 months. The APR shows the equivalent annual rate that accounts for this earlier repayment pattern.
                </p>

                <p className="text-sm text-gray-700">
                  This difference helps you compare loans fairly, as it shows the actual annual cost regardless of how payments are structured.
                  {results.calculatorType === 'missed' && " The APR may increase further due to missed payments, late fees, and other charges."}
                  {results.calculatorType === 'restructure' && " Loan restructuring can change the effective APR due to changes in term length or payment structure."}
                </p>
              </div>
            )}
            
            {results.calculatorType === 'early' && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-green-600 mb-2">Early Repayment Benefits</h4>
                <p className="text-sm text-gray-700">
                  Making early payments on your loan can result in significant interest savings. The full settlement option of{' '}
                  <span className="font-medium">{formatCurrency(results.fullSettlementAmount)}</span> would save you{' '}
                  <span className="font-medium">{formatCurrency(results.interestSavingsFromFullSettlement)}</span> in interest payments.
                </p>
                {results.earlyPaymentAmount > 0 && (results.reducePaymentScenario || results.reduceTermScenario) && (
                  <p className="text-sm text-gray-700 mt-2">
                    A partial early payment of {formatCurrency(results.earlyPaymentAmount)} provides flexibility - you can either reduce your monthly payment burden or pay off your loan faster while maintaining the same payment amount.
                  </p>
                )}
              </div>
            )}
            
            {results.calculatorType === 'missed' && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-red-600 mb-2">Missed Payment Impact</h4>
                <p className="text-sm text-gray-700">
                  Missing {results.missedPayments.length} payment{results.missedPayments.length > 1 ? 's' : ''} will extend your loan by{' '}
                  <span className="font-medium">{String(results.additionalMonths)} months</span>, 
                  adding <span className="font-medium">{formatCurrency(results.additionalInterest)}</span> in additional interest
                  {results.totalLateFees && !results.totalLateFees.isZero() ? 
                    <span> plus <span className="font-medium">{formatCurrency(results.totalLateFees)}</span> in late fees (which also accrue interest over the extended term)</span> : ''}.
                  Your monthly payment amount remains unchanged.
                </p>
                {results.totalLateFees && !results.totalLateFees.isZero() && (
                  <p className="text-sm text-gray-700 mt-2">
                    <strong>Total additional cost:</strong> {formatCurrency(results.totalLateFees.plus(results.additionalInterest))}
                    (late fees + interest on extended term).
                  </p>
                )}
                {results.effectiveAPR && results.effectiveAPR > results.apr && (
                  <p className="text-sm text-gray-700 mt-2">
                    The effective APR with missed payments increases from {results.apr.toString()}% to{' '}
                    <span className="font-medium">{results.effectiveAPR.toFixed ? results.effectiveAPR.toFixed(2) : results.effectiveAPR.toString()}%</span>.
                  </p>
                )}
              </div>
            )}
            
            {results.calculatorType === 'restructure' && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-blue-600 mb-2">Restructuring Information</h4>
                <p className="text-sm text-gray-700">
                  {results.restructureType === 'spread' && 
                    `Spreading arrears over your remaining term will increase your monthly payment by ${formatCurrency(results.increasedMonthlyAmount)} (${typeof results.percentageIncrease === 'object' ? results.percentageIncrease.toFixed(2) : (parseFloat(results.percentageIncrease)).toFixed(2)}%).`
                  }
                  {results.restructureType === 'extend' && 
                    `Extending your loan term will add ${String(results.additionalMonths)} additional months to your repayment period.`
                  }
                  {results.restructureType === 'reduce' && 
                    `Reducing your monthly payment will extend your loan term by ${String(results.additionalMonths)} months.`
                  }
                </p>
                {results.additionalCost && results.additionalCost > 0 && (
                  <p className="text-sm text-gray-700 mt-2">
                    <span className="font-medium text-red-600">Additional Cost:</span> This restructuring will cost an extra {formatCurrency(results.additionalCost)} compared to your original loan terms
                    {results.additionalInterest && results.additionalInterest > 0 && 
                      `, including ${formatCurrency(results.additionalInterest)} in additional interest due to the extended term`
                    }.
                  </p>
                )}
                {results.effectiveAPR && results.effectiveAPR !== results.apr && (
                  <p className="text-sm text-gray-700 mt-2">
                    {results.effectiveAPR > results.apr ? 
                      `The effective APR after restructuring increases from ${results.apr.toString()}% to ${results.effectiveAPR.toFixed ? results.effectiveAPR.toFixed(2) : results.effectiveAPR.toString()}%.` :
                      `The effective APR after restructuring changes from ${results.apr.toString()}% to ${results.effectiveAPR.toFixed ? results.effectiveAPR.toFixed(2) : results.effectiveAPR.toString()}%.`
                    }
                  </p>
                )}
              </div>
            )}
            
            {results.calculatorType === 'interest' && results.includeInterestFreeze && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-green-600 mb-2">Interest Freeze Benefit</h4>
                <p className="text-sm text-gray-700">
                  The interest freeze from month {String(results.freezeStartMonth)} to {String(results.freezeEndMonth)} will save you approximately{' '}
                  <span className="font-medium">{formatCurrency(results.interestSavings)}</span> in interest.
                </p>
                {results.effectiveAPR && results.effectiveAPR < results.apr && (
                  <p className="text-sm text-gray-700 mt-2">
                    The effective APR with interest freeze decreases from {results.apr.toString()}% to{' '}
                    <span className="font-medium">{results.effectiveAPR.toFixed ? results.effectiveAPR.toFixed(2) : results.effectiveAPR.toString()}%</span>.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPanel;